import { AdditionalServicesSection } from "components/AdditionalServicesSection";
import { DetailsSection } from "components/DetailsSection";
import { ActionButton } from "components/generic/ActionButton";
import { SecondaryOptionButton } from "components/generic/SecondaryOptionButton";
import { Hero } from "components/generic/Hero";
import { ModalCentered } from "components/generic/ModalCentered";
import { VehicleDetailsSection } from "components/VehicleDetailsSection";
import { convertWorkRequiredToLines } from "helpers/WorkHelper";
import React, { useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import { scrollToTop } from "helpers/ScrollingHelper";
import dayjs from "dayjs";
import { Icons } from "components/icons/Icons";
import { AdditionalServiceType, CreateBookingResponse, NewBookingRequest } from "generated/types/types";
import { assertNotNullOrUndefined, formattedContactPreference } from "helpers/Helpers";
import { CompleteNewBooking } from "types/NewBooking";
import { http } from "api/HttpClient";
import { useTranslation } from "react-i18next";

interface Props extends INewBookingPropsBase {
    setEncryptedBookingId: (value: string) => void;
    micrositePin: string;
    currentInputData: CompleteNewBooking;
    goToCalendar: () => void;
    referenceCode: string;
}

/**
 * A component for displaying a summary of a booking request that the user is considering submitting.
 * The intent is that they can review the booking request here before deciding whether to submit it.
 */
export const Summary: React.FC<Props> = (props: Props) => {
    const vehicleDetails = props.currentInputData.vehicleDetails;
    const workDetails = props.currentInputData.workDetails;
    const serviceCentreDetails = props.currentInputData.selectedServiceCentreDetails;
    const additionalServiceRequested = props.currentInputData.workDetails?.additionalServiceRequested ?? AdditionalServiceType.NoAdditional;
    const collectionAddress = props.currentInputData.workDetails?.collectionAddress ?? null;
    const driverDetails = props.currentInputData.driverDetails;
    const contactPreferences = props.currentInputData.driverDetails?.contactPreferences ?? [];
    const [showCancel, setShowCancel] = React.useState(false);
    const [actionsDisabled, setActionsDisabled] = React.useState(false);
    const [showNotConfirmedModal, setShowNotConfirmedModal] = React.useState(false);
    const { t } = useTranslation();

    useEffect(() => scrollToTop(), []);

    const getCancelContents = () => {
        return (
            <Modal.Body>
                <h1>{t("summary_restart")}</h1>
                <h2>{t("summary_reenter")}</h2>
                <Button
                    size="lg"
                    onClick={() => {
                        window.location.reload();
                    }}
                    disabled={actionsDisabled}
                >
                    {t("summary_yes")}
                </Button>
                <p className="link-muted" onClick={() => setShowCancel(false)}>
                    {t("summary_no")}
                </p>
            </Modal.Body>
        );
    };

    const handleCreateBooking = () => {
        //Validate the request data available
        setActionsDisabled(true);
        const inputData = props.currentInputData;

        const requestData: NewBookingRequest = {
            driverMicrositePin: props.micrositePin,
            vehicleRegNo: inputData.vehicleDetails.registrationNumber,
            assetId: inputData.vehicleDetails.assetId,
            odoReading: Number.parseInt(assertNotNullOrUndefined(inputData.vehicleDetails.odoReading)),
            selectedServiceCentreId: inputData.selectedServiceCentreDetails.id,

            driverName: inputData.driverDetails.driverName,
            phoneNumber: inputData.driverDetails.phoneNumber,
            smsMobileNo: inputData.driverDetails.smsMobileNo,
            email: inputData.driverDetails.email,
            contactPreferences: inputData.driverDetails.contactPreferences,
            bookerName: inputData.driverDetails.bookerName,

            requestedBookingDates: assertNotNullOrUndefined(inputData.workDetails.requestedBookingDates?.map(date => dayjs(date).format("YYYY-MM-DDTHH:mm:ss"))),
            workRequired: inputData.workDetails.workRequired,
            repairOtherText: inputData.workDetails.repairOtherText ?? null,
            additionalServiceRequested: inputData.workDetails.additionalServiceRequested,
            collectionAddress: inputData.workDetails.collectionAddress ?? null,
            collectionContactName: inputData.workDetails.collectionContactName ?? null,
            collectionContactNumber: inputData.workDetails.collectionContactNumber ?? null,
            collectionInstructions: inputData.workDetails.collectionInstruction ?? null,
            additionalNotes: inputData.workDetails.additionalNotes ?? null,

            previousBookingId: inputData.previousBookingId,
            ruleId: inputData.selectedServiceCentreDetails.ruleId,
            ruleParameterValueId: inputData.selectedServiceCentreDetails.ruleParameterValueId,
            ruleResultValueId: inputData.selectedServiceCentreDetails.ruleResultValueId,
            safetyRecall: inputData.workDetails.safetyRecall,
            isRequestDiaryIntegrated: inputData.workDetails?.isDiaryIntegrated || false,
            temporaryBookingReference: inputData.tempBookingReference,
            referenceCode: props.referenceCode
        };

        http.post<CreateBookingResponse>("/Booking/CreateBookingWithDiaryIntegration", requestData)
            .then(({ data }) => {
                if (data.isFromDiaryIntegration && data.errorResponse?.errorCode === 100) {
                    setShowNotConfirmedModal(true);
                    return;
                }
                if (data.result) {
                    props.setEncryptedBookingId(data.result);
                    props.goToNextStep();
                }
            });
    };

    return (
        <div className="row no-gutters">
            <div className="col-12">
                <Hero heading={t("summary_booking_summary")} description={t("summary_details_correct")} />
                {vehicleDetails ? <VehicleDetailsSection vehicleDetails={vehicleDetails} /> : null}
                {serviceCentreDetails ? (
                    <DetailsSection
                        iconComponent={Icons.GarageIcon}
                        heading={t("summary_chosen_garage")}
                        lines={[{ isHighlighted: true, text: serviceCentreDetails.name }, { text: serviceCentreDetails.addressLines.join(", ") }]}
                    />
                ) : null}
                {workDetails ? (
                    <DetailsSection
                        iconComponent={Icons.JobsIcon}
                        heading={t("summary_work_details")}
                        lines={[
                            ...convertWorkRequiredToLines(workDetails?.workRequired ?? [], workDetails.repairOtherText ?? "", t),
                            ...(workDetails.safetyRecall ? [{ text: workDetails.safetyRecallText }] : []),
                            { text: t("summary_following_dates"), isBold: true }, // Bold text indicated with isBold
                            ...(workDetails?.requestedBookingDates?.map((x) => ({
                                text: dayjs(x).format("ddd Do MMM YYYY [at] HH:mm"),
                            })) ?? []),
                        ]}
                    />
                ) : null}
                <AdditionalServicesSection
                    additionalServiceType={additionalServiceRequested}
                    collectionAddressLines={collectionAddress?.split("\n") ?? null}
                    additionalNotes={workDetails.additionalNotes}
                />
                {driverDetails ? (
                    <>
                        <DetailsSection
                            iconComponent={Icons.DriverIcon}
                            heading={t("summary_driver_details")}
                            lines={[
                                { text: driverDetails.driverName },
                                { text: driverDetails.email },
                                { text: driverDetails.phoneNumber },
                                { prefix: t("summary_contacted_by"), text: formattedContactPreference(contactPreferences) }
                            ]}
                        />

                        {driverDetails.bookerName ? (
                            <div style={{ marginTop: "20px" }}>
                                <h5>{t("summary_service_desk")}</h5>
                                <p>{driverDetails.bookerName}</p>
                            </div>
                        ) : null}
                    </>
                ) : null}
                <div className="row section">
                    <ActionButton label={t("summary_request_booking")} onClick={handleCreateBooking} disabled={actionsDisabled} />
                    <div className="col-12">
                        <SecondaryOptionButton text={t("summary_restart")} onClick={() => setShowCancel(true)} />
                    </div>

                    {/* <ActionButton label="Restart Booking Request" onClick={() => setCancelShow(true)} disabled={actionsDisabled} /> */}
                </div>
                <ModalCentered size="sm" show={showCancel} onHide={() => setShowCancel(false)}>
                    <div className="custom-dialog">
                        <Modal.Header closeButton />
                        {getCancelContents()}
                    </div>
                </ModalCentered>
                <ModalCentered size="sm" show={showNotConfirmedModal} onHide={props.goToCalendar} backdrop="static">
                    <div className="custom-dialog">
                        <Modal.Body>
                            <h1>{t("summary_not_confirmed")}</h1>
                            <h2>{t("summary_desc")}</h2>
                            <Button size="lg" onClick={props.goToCalendar}>
                                {t("summary_return_calendar")}
                            </Button>
                        </Modal.Body>
                    </div>
                </ModalCentered>
            </div>
        </div>
    );
};
